import React, { useSyncExternalStore } from 'react'
import { Stack, Typography } from '@mui/material'
import LegendItem from 'src/Libs/chart-kit/shared/components/LegendItem'
import { AI360_LINE_CHART_CONFIG } from 'src/Libs/ai-360/consts'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { formatYTicks } from 'src/Libs/ai-360/utils'
import { LineChartStore } from 'src/Libs/ai-360/store'
import { DEFAULT_HUMAN_TIME, format } from '@e3dc-react/shell/Libs/date'
import { useUserSettings } from 'src/Hooks'

/**
 * Custom legend for AI360 line chart, showing the label, value, and unit for each line in the
 * line chart based on the tooltip payload.
 */
const LegendAI360: React.FC = () => {
  const { t } = useCustomTranslation()
  const { updateUserSettings } = useUserSettings()
  const { time, solarForecast, solarProduction, powerPrice, isLegendEnabled } = useSyncExternalStore(LineChartStore.subscribe, LineChartStore.get)
  const formattedTime = time ? format(time, DEFAULT_HUMAN_TIME) : ''

  const onToggleLegend = (key: keyof typeof isLegendEnabled): void => {
    LineChartStore.set({
      isLegendEnabled: {
        ...isLegendEnabled,
        [key]: !isLegendEnabled[key],
      },
    })
    updateUserSettings({ isAI360DetailsChartLegendEnabled: { ...isLegendEnabled, [key]: !isLegendEnabled[key] } })
  }

  /* show title for solar production when legend is disabled */
  const solarForecastTitle = isLegendEnabled.solarData
    ? t(AI360_LINE_CHART_CONFIG.solarForecast.label)
    : t(AI360_LINE_CHART_CONFIG.solarProduction.label)

  return (
    <Stack gap={2} flexDirection="column" ml={2}>
      <Typography variant="subtitle1">{formattedTime}</Typography>
      <LegendItem
        type={'line'}
        title={t(AI360_LINE_CHART_CONFIG.powerPrice.label)}
        color={AI360_LINE_CHART_CONFIG.powerPrice.color ?? ''}
        value={powerPrice === null ? undefined : formatYTicks(powerPrice, AI360_LINE_CHART_CONFIG.powerPrice.unit)}
        isDisabled={!isLegendEnabled.powerPrice}
        onClick={() => onToggleLegend('powerPrice')}
      />

      {solarProduction !== null && (
        <LegendItem
          type={'line'}
          title={t(AI360_LINE_CHART_CONFIG.solarProduction.label)}
          color={AI360_LINE_CHART_CONFIG.solarProduction.color ?? ''}
          value={formatYTicks(solarProduction, AI360_LINE_CHART_CONFIG.solarProduction.unit)}
          isDisabled={!isLegendEnabled.solarData}
          onClick={() => onToggleLegend('solarData')}
        />
      )}
      {solarForecast !== null && (
        <LegendItem
          type={'line'}
          title={solarForecastTitle}
          color={AI360_LINE_CHART_CONFIG.solarForecast.color ?? ''}
          value={formatYTicks(solarForecast, AI360_LINE_CHART_CONFIG.solarForecast.unit)}
          isDisabled={!isLegendEnabled.solarData}
          onClick={() => onToggleLegend('solarData')}
        />
      )}
      <LegendItem
        type={'rectangle'}
        title={t(AI360_LINE_CHART_CONFIG.feedInPrice.label)}
        color={AI360_LINE_CHART_CONFIG.feedInPrice.color ?? ''}
        isDisabled={!isLegendEnabled.feedInPrice}
        onClick={() => onToggleLegend('feedInPrice')}
      />
    </Stack>
  )
}

export default LegendAI360
